import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAutocomplete from "components/MDAutocomplete";
import MDTextField from "components/MDTextField";
import {
  Grid,
  Card,
  Box,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Tooltip,
  Divider,
  Stack,
  Autocomplete,
  TextField,
  Button,
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { MaterialReactTable } from "material-react-table";
import { useMemo } from "react";
import handleExport from "utils/exportData";
import { IconTableExport } from "@tabler/icons-react";
import { compressImage } from "utils/imageCompression";
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import LinkIcon from "@mui/icons-material/Link";

function EventsAdd() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [errors, setErrors] = useState({});

  // Form States
  const [eventName, setEventName] = useState(""); // For Admin
  const [selectedStatus, setSelectedStatus] = useState(null); // For Admin
  const [selectedEvent, setSelectedEvent] = useState(null); // For User
  const [participantsCount, setParticipantsCount] = useState("");
  const [conductedDate, setConductedDate] = useState(dayjs());
  const [description, setDescription] = useState(""); // For User
  const [driveUrl, setDriveUrl] = useState(""); // For User (Video Link) - comma separated string for backend
  const [videoInput, setVideoInput] = useState(""); // Current input in the text field
  const [videoUrls, setVideoUrls] = useState([]); // Array of validated URLs for UI
  const [files, setFiles] = useState([]);

  // Admin Table States
  const [adminData, setAdminData] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // Admin Edit Mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);

  // Submission Edit Mode
  const { submission_id, user_id: submission_user_id } = useParams();
  const isSubmissionEdit = Boolean(submission_id && submission_user_id);
  const [existingUploads, setExistingUploads] = useState([]);

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Fetch current user
  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  };

  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: me,
    retry: false,
  });

  // Fallback: Fetch complete user profile to extract reliable district data if cookie/token is incomplete
  const { data: fullUserData } = useQuery({
    queryKey: ["userDetails", meData?.user_id],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/${meData.user_id}`, {
        withCredentials: true,
      });
      return response.data.data;
    },
    enabled: !!meData?.user_id,
  });

  const userDistrict = meData?.district || fullUserData?.district || "";

  const isAdmin = meData?.role_id === 1;

  const clearForm = () => {
    setEventName("");
    setSelectedStatus(null);
    setSelectedEvent(null);
    setParticipantsCount("");
    setConductedDate(dayjs());
    setDescription("");
    setDriveUrl("");
    setVideoInput("");
    setVideoUrls([]);
    setFiles([]);
    setIsEditMode(false);
    setEditingEventId(null);
    setErrors({});
    setExistingUploads([]);
    if (isSubmissionEdit) navigate("/events/add");
  };

  // Fetch Statuses (for Admin)
  const fetchStatus = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/status/all`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data.data;
  };

  const {
    data: fetchedStatus,
    error: statusError,
    isLoading: statusIsLoading,
  } = useQuery({
    queryKey: ["fetchStatus"],
    queryFn: fetchStatus,
    retry: false,
    enabled: !!meData && meData.role_id === 1,
  });

  // Fetch Events List for Admin Table
  const fetchAdminEvents = async (
    pageIndex = pagination.pageIndex,
    pageSize = pagination.pageSize,
    filters = columnFilters,
    sort = sorting
  ) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pageIndex + 1,
        limit: pageSize,
        columnFilters: JSON.stringify(filters),
      });

      if (sort.length > 0) {
        queryParams.append("sortField", sort[0].id);
        queryParams.append("sortOrder", sort[0].desc ? "desc" : "asc");
      }

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/events/list?${queryParams.toString()}`, {
        withCredentials: true,
      });

      if (response.data.status === 200) {
        setAdminData(response.data.data || []);
        setRowCount(response.data.totalCount || 0);
      }
    } catch (err) {
      console.error("Error fetching admin events:", err);
    }
    setLoading(false);
  };

  const { refetch: refetchAdminEvents } = useQuery({
    queryKey: ["adminEvents", pagination, columnFilters, sorting],
    queryFn: () => fetchAdminEvents(pagination.pageIndex, pagination.pageSize, columnFilters, sorting),
    enabled: !!meData && meData.role_id === 1,
    keepPreviousData: true,
  });

  // Fetch Events (for User dropdown)
  const fetchEvents = async () => {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/events/all`, {
      withCredentials: true,
    });
    return response.data.data;
  };

  const { data: eventsList, refetch: refetchEvents } = useQuery({
    queryKey: ["events"],
    queryFn: fetchEvents,
    enabled: !!meData,
  });

  const handleFileChange = (e) => {
    const uploadedFiles = Array.from(e.target.files);

    // Check size and type
    const MAX_SIZE = 25 * 1024 * 1024; // 25MB
    const ALLOWED_TYPES = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];

    for (const file of uploadedFiles) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setSnackbar({ open: true, message: `File type not supported: ${file.name}. Only Photos, PDFs, and Word documents allowed.`, severity: "error" });
        return;
      }
      if (file.size > MAX_SIZE) {
        setSnackbar({ open: true, message: `File too large: ${file.name}. Maximum size allowed is 25MB.`, severity: "error" });
        return;
      }
    }

    setFiles((prev) => [...prev, ...uploadedFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingUpload = (index) => {
    setExistingUploads((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddVideoUrl = () => {
    if (!videoInput.trim()) return;

    // Simple URL validation
    try {
      new URL(videoInput);
    } catch (e) {
      setSnackbar({ open: true, message: "Please enter a valid URL", severity: "error" });
      return;
    }

    setVideoUrls((prev) => [...prev, videoInput]);
    setVideoInput("");
  };

  const removeVideoUrl = (index) => {
    setVideoUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};
    const isUser = meData?.role_id !== 1;

    if (isAdmin) {
      if (!eventName || !eventName.trim()) newErrors.eventName = "Event name is required.";
      if (!selectedStatus) newErrors.selectedStatus = "Status is required.";
    }

    if (isUser) {
      if (!selectedEvent) newErrors.selectedEvent = "Please select an event.";
      if (!participantsCount) newErrors.participantsCount = "Participants count is required.";
      else if (parseInt(participantsCount) <= 0) newErrors.participantsCount = "Participants count must be a positive number.";
      if (!conductedDate) newErrors.conductedDate = "Conducted date is required.";
      if (description && description.length > 500) newErrors.description = "Description must be 500 characters or less.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fetch Submission Details for Edit
  useEffect(() => {
    const fetchSubmissionDetails = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/event-submissions/get/${submission_id}/${submission_user_id}`,
          { withCredentials: true }
        );
        if (res.data.status === 200) {
          const sub = res.data.data;
          setSelectedEvent({ title: sub.event_name, value: sub.event_id });
          setParticipantsCount(sub.no_of_participants || "");
          setConductedDate(dayjs(sub.conducted_date));
          setDescription(sub.description || "");
          const dUrl = sub.drive_url || "";
          setDriveUrl(dUrl);
          setVideoUrls(dUrl ? dUrl.split(",").filter(u => u.trim() !== "") : []);
          setExistingUploads(sub.uploads || []);
        }
      } catch (err) {
        console.error("Error fetching submission details:", err);
        setSnackbar({ open: true, message: "Failed to fetch submission details.", severity: "error" });
      } finally {
        setLoading(false);
      }
    };

    if (isSubmissionEdit) {
      fetchSubmissionDetails();
    }
  }, [isSubmissionEdit, submission_id, submission_user_id]);

  // Set default status to 'Active' for Admins (if not in Edit Mode)
  useEffect(() => {
    if (isAdmin && !isEditMode && fetchedStatus && fetchedStatus.length > 0 && !selectedStatus) {
      const activeStatus = fetchedStatus.find(s => s.name?.toLowerCase() === "active");
      if (activeStatus) {
        setSelectedStatus({ title: activeStatus.name, value: activeStatus.status_id });
      }
    }
  }, [fetchedStatus, isAdmin, isEditMode, selectedStatus]);

  // Handle default selection from navigation state (Notification click)
  useEffect(() => {
    if (location.state?.field_id && eventsList && eventsList.length > 0) {
      const fieldId = Number(location.state.field_id);
      const matchedEvent = eventsList.find(e => Number(e.value) === fieldId);
      if (matchedEvent) {
        setSelectedEvent(matchedEvent);
        // Clear state to avoid re-selecting if the user navigates back and forth
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, eventsList]);

  const handleSubmit = async () => {
    if (!meData?.user_id) {
      setSnackbar({ open: true, message: "User authentication failed.", severity: "error" });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (isAdmin) {
        if (isEditMode) {
          // Admin: Update Event
          const res = await axios.put(
            `${process.env.REACT_APP_API_URL}/events/update/${editingEventId}`,
            {
              event_name: eventName,
              status: selectedStatus.value,
              updated_by: meData.user_id,
              updated_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
            },
            { withCredentials: true }
          );
          setSnackbar({ open: true, message: res.data.message || "Event updated successfully!", severity: "success" });
        } else {
          // Admin: Create Event
          const res = await axios.post(
            `${process.env.REACT_APP_API_URL}/events/add`,
            {
              event_name: eventName,
              status: selectedStatus.value,
              user_id: meData.user_id,
              created_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
            },
            { withCredentials: true }
          );
          setSnackbar({ open: true, message: res.data.message || "Event created successfully!", severity: "success" });
        }
        clearForm();
        refetchAdminEvents();
        refetchEvents();
      } else {
        // User: Submit or Update Event Participation
        const formData = new FormData();
        formData.append("event_id", selectedEvent.value);
        formData.append("no_of_participants", participantsCount);
        formData.append("conducted_date", conductedDate.format("YYYY-MM-DD"));
        formData.append("description", description);
        formData.append("drive_url", videoUrls.join(","));
        formData.append("user_id", isSubmissionEdit ? submission_user_id : meData.user_id);
        formData.append("district_id", userDistrict);

        if (isSubmissionEdit) {
          formData.append("existing_uploads", JSON.stringify(existingUploads));
        }

        const compressedFiles = await Promise.all(
          files.map(file => compressImage(file))
        );

        compressedFiles.forEach((file) => {
          formData.append("uploads", file);
        });

        const url = isSubmissionEdit
          ? `${process.env.REACT_APP_API_URL}/event-submissions/update/${submission_id}/${meData.user_id}`
          : `${process.env.REACT_APP_API_URL}/event-submissions/add`;

        const method = isSubmissionEdit ? 'put' : 'post';

        const res = await axios({
          method,
          url,
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });

        setSnackbar({ open: true, message: res.data.message || "Operation successful!", severity: "success" });
        clearForm();
        if (isAdmin) refetchAdminEvents();
        refetchEvents();
      }

      if (!isAdmin) {
        setTimeout(() => navigate("/events/list"), 2000);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Something went wrong.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      // For export, we usually want all data matching the current filters but without pagination
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 10000,
        columnFilters: JSON.stringify(columnFilters),
      });

      if (sorting.length > 0) {
        queryParams.append("sortField", sorting[0].id);
        queryParams.append("sortOrder", sorting[0].desc ? "desc" : "asc");
      }

      const res = await axios.get(`${process.env.REACT_APP_API_URL}/events/list?${queryParams.toString()}`, {
        withCredentials: true,
      });

      if (res.data.status === 200) {
        await handleExport(res.data.data, "Events_List_Data", [], fetchedStatus?.map(s => ({ value: s.status_id, title: s.name })));
        setSnackbar({ open: true, message: "Excel downloaded successfully!", severity: "success" });
      }
    } catch (error) {
      console.error("Export error:", error);
      setSnackbar({ open: true, message: "Something went wrong during export.", severity: "error" });
    }
  };

  const handleEdit = async (event) => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/events/get/${event.id || event.event_id}`, {
        withCredentials: true,
      });

      if (response.data.status === 200) {
        const eventData = response.data.data;
        setIsEditMode(true);
        setEditingEventId(eventData.id || eventData.event_id);
        setEventName(eventData.name || eventData.event_name);
        const status = fetchedStatus?.find((s) => s.status_id === eventData.status);
        setSelectedStatus(status ? { title: status.name, value: status.status_id } : null);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to fetch event details.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Event Name",
        // eslint-disable-next-line react/prop-types
        Filter: ({ column }) => (
          <Autocomplete
            options={eventsList || []}
            getOptionLabel={(option) => option.title || ""}
            // eslint-disable-next-line react/prop-types
            value={eventsList?.find((e) => e.title === column.getFilterValue()) || null}
            onChange={(event, newValue) => {
              // eslint-disable-next-line react/prop-types
              column.setFilterValue(newValue ? newValue.title : undefined);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Event"
                variant="standard"
                sx={{
                  "& .MuiInputBase-root": {
                    color: "#fff",
                    fontSize: "12px",
                  },
                  "& .MuiInput-underline:before": {
                    borderBottomColor: "#e0e0e0",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#fff",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#fff",
                  },
                }}
              />
            )}
            sx={{ minWidth: "150px" }}
          />
        ),
        Cell: ({ cell, row }) => cell.getValue() || row.original.event_name || "N/A",
        size: 250,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 150,
        // eslint-disable-next-line react/prop-types
        Filter: ({ column }) => (
          <Autocomplete
            options={fetchedStatus || []}
            getOptionLabel={(option) => option.name || ""}
            // eslint-disable-next-line react/prop-types
            value={fetchedStatus?.find((s) => s.status_id === column.getFilterValue()) || null}
            onChange={(event, newValue) => {
              // eslint-disable-next-line react/prop-types
              column.setFilterValue(newValue ? newValue.status_id : undefined);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Status"
                variant="standard"
                sx={{
                  "& .MuiInputBase-root": {
                    color: "#fff",
                    fontSize: "12px",
                  },
                  "& .MuiInput-underline:before": {
                    borderBottomColor: "#e0e0e0",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#fff",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#fff",
                  },
                }}
              />
            )}
            sx={{ minWidth: "100px" }}
          />
        ),
        Cell: ({ cell }) => {
          const status = fetchedStatus?.find((s) => s.status_id === cell.getValue());
          return status ? status.name : cell.getValue() || "N/A";
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 100,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <MDBox display="flex" gap={1}>
            <Tooltip title="Edit Event">
              <IconButton color="info" size="small" onClick={() => handleEdit(row.original)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MDBox>
        ),
      },
    ],
    [fetchedStatus, eventsList]
  );

  return (
    <DashboardLayout>
      <MDBox mt={4} mb={3}>
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h4" fontWeight="bold">
            {isAdmin ? "Create New Event" : "Event Submission"}
          </MDTypography>
        </MDBox>

        <Card>
          <MDBox p={{ xs: 2, sm: 3, md: 4 }}>
            <Stack spacing={4}>
              {isAdmin ? (
                // Admin Form
                <Grid container spacing={3}>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <MDTextField
                      name="eventName"
                      placeholder="Event Name"
                      displayName="Event Name"
                      value={eventName}
                      onChange={(value) => {
                        setEventName(value);
                        if (errors.eventName) setErrors({ ...errors, eventName: "" });
                      }}
                      required={true}
                      error={!!errors.eventName}
                      helperText={errors.eventName}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <MDAutocomplete
                      label="Status"
                      name="status"
                      options={fetchedStatus?.map(s => ({ title: s.name, value: s.status_id })) || []}
                      value={selectedStatus?.value}
                      onChange={(value) => {
                        const status = fetchedStatus.find(s => s.status_id === value);
                        setSelectedStatus(status ? { title: status.name, value: status.status_id } : null);
                        if (errors.selectedStatus) setErrors({ ...errors, selectedStatus: "" });
                      }}
                      placeholder="Select Status"
                      required={true}
                      error={!!errors.selectedStatus}
                      helperText={errors.selectedStatus}
                    />
                  </Grid>
                </Grid>
              ) : (
                // User Form
                <Grid container spacing={3}>
                  <Grid item size={{ xs: 12, md: 12 }}>
                    <MDAutocomplete
                      label="Select Event"
                      name="event"
                      options={eventsList || []}
                      value={selectedEvent?.value}
                      onChange={(value) => {
                        const event = eventsList?.find(e => e.value === value);
                        setSelectedEvent(event || null);
                        if (errors.selectedEvent) setErrors({ ...errors, selectedEvent: "" });
                      }}
                      placeholder="Choose an event"
                      required={true}
                      error={!!errors.selectedEvent}
                      helperText={errors.selectedEvent}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <MDTextField
                      name="participantsCount"
                      placeholder="Number of Participants"
                      displayName="Number of Participants"
                      value={participantsCount}
                      onChange={(value) => {
                        setParticipantsCount(value);
                        if (errors.participantsCount) setErrors({ ...errors, participantsCount: "" });
                      }}
                      required={true}
                      inputType="onlyNumbers"
                      error={!!errors.participantsCount}
                      helperText={errors.participantsCount}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <MDBox mb={1}>
                      <MDTypography style={{ fontSize: "16px" }} color="dark">
                        Conducted Date <span style={{ color: "red" }}>*</span>
                      </MDTypography>
                    </MDBox>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        format="DD-MM-YYYY"
                        fullWidth
                        value={conductedDate}
                        onChange={(newValue) => {
                          setConductedDate(newValue);
                          if (errors.conductedDate) setErrors({ ...errors, conductedDate: "" });
                        }}
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            InputProps: {
                              sx: {
                                height: "50px",
                                fontSize: "14px",
                                fontWeight: "150",
                              },
                            },

                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>

                  {/* Row 3: Description */}
                  <Grid item size={{ xs: 12, md: 12 }}>
                    <MDBox mb={1}>
                      <MDTypography style={{ fontSize: "16px" }} color="dark">
                        Description
                      </MDTypography>
                    </MDBox>
                    <TextField
                      fullWidth
                      placeholder="Enter Event Description (Max 500 characters)"
                      name="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      multiline
                      rows={5}
                      inputProps={{ maxLength: 500 }}
                      error={!!errors.description}
                      helperText={errors.description || `${description.length}/500`}
                      FormHelperTextProps={{
                        sx: {
                          textAlign: "right",
                          marginRight: 0,
                          display: "block",
                          fontSize: "0.75rem",
                        },
                      }}
                    />
                  </Grid>

                  {/* Row 4: Video Link Note & Field */}
                  <Grid item size={{ xs: 12, md: 12 }}>
                    <MDBox mb={2} p={2} sx={{ backgroundColor: "#e3f2fd", borderRadius: 1, border: "1px solid #90caf9" }}>
                      <MDTypography variant="body2" color="info" fontWeight="medium">
                        <strong>Note:</strong> Direct video uploads are not supported. Please provide Google Drive links (with &quot;Anyone with the link can view&quot; permission) or other video URLs for your submission.
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" gap={1} mb={2}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Paste Video URL (YouTube, Drive, etc.)"
                        value={videoInput}
                        onChange={(e) => setVideoInput(e.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "45px",
                            backgroundColor: "white",
                          }
                        }}
                      />
                      <MDButton
                        variant="gradient"
                        color="info"
                        onClick={handleAddVideoUrl}
                        disabled={!videoInput.trim()}
                        sx={{ height: "45px" }}
                      >
                        Add Video
                      </MDButton>
                    </MDBox>

                    {videoUrls.length > 0 && (
                      <MDBox mb={2} p={2} sx={{ border: "1px solid #ddd", borderRadius: 1, backgroundColor: "#f9f9f9" }}>
                        <MDTypography variant="h6" fontWeight="bold" mb={1} sx={{ fontSize: "14px" }}>
                          Added Video Links:
                        </MDTypography>
                        <MDBox display="flex" flexWrap="wrap" gap={1.5}>
                          {videoUrls.map((url, index) => {
                            const lowerUrl = url.toLowerCase();
                            let IconComponent = LinkIcon;
                            let iconColor = "info";

                            if (lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be")) {
                              IconComponent = YouTubeIcon;
                              iconColor = "error";
                            } else if (lowerUrl.includes("drive.google.com")) {
                              IconComponent = InsertDriveFileIcon;
                              iconColor = "info";
                            } else if (lowerUrl.match(/\.(mp4|mov|quicktime)$/)) {
                              IconComponent = PlayCircleIcon;
                              iconColor = "info";
                            }

                            return (
                              <MDBox
                                key={index}
                                position="relative"
                                sx={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: 1,
                                  border: "1px solid #ccc",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "white",
                                  cursor: "pointer"
                                }}
                                onClick={() => window.open(url, '_blank')}
                              >
                                <IconComponent color={iconColor} sx={{ fontSize: 32 }} />
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeVideoUrl(index);
                                  }}
                                  sx={{
                                    position: "absolute",
                                    top: -8,
                                    right: -8,
                                    backgroundColor: "error.main",
                                    color: "white",
                                    padding: "2px",
                                    "&:hover": { backgroundColor: "error.dark" }
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </MDBox>
                            );
                          })}
                        </MDBox>
                      </MDBox>
                    )}
                  </Grid>

                  {/* Row 5: Upload Section */}
                  <Grid item size={{ xs: 12, md: 12 }}>
                    <MDBox mb={1}>
                      <MDTypography variant="h6" fontWeight="medium" sx={{ fontSize: "16px" }}>
                        Upload Files (Photos / PDFs / Docs)
                      </MDTypography>
                    </MDBox>
                    <Box
                      sx={{
                        border: "2px dashed #1A73E8",
                        borderRadius: 2,
                        p: 3,
                        textAlign: "center",
                        backgroundColor: "#f8f9fa",
                        cursor: "pointer",
                        transition: "0.3s",
                        "&:hover": { backgroundColor: "#f1f3f4" },
                      }}
                      onClick={() => document.getElementById("file-upload").click()}
                    >
                      <CloudUploadIcon sx={{ fontSize: 40, color: "#1A73E8", mb: 1 }} />
                      <MDTypography variant="body2" color="text">
                        Click to upload or drag and drop
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Supported: PNG, JPG, JPEG, PDF, DOC, DOCX (Max 25MB per file)
                      </MDTypography>
                      <input
                        id="file-upload"
                        type="file"
                        multiple
                        accept="image/*,application/pdf,.doc,.docx"
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                      />
                    </Box>

                    {(files.length > 0 || existingUploads.length > 0) && (
                      <MDBox mt={3}>
                        <MDTypography variant="h6" fontWeight="bold" mb={2}>
                          File Previews
                        </MDTypography>
                        <Grid container spacing={2}>
                          {/* Existing Uploads */}
                          {existingUploads.map((url, idx) => {
                            const isImage = /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(url.split('?')[0]);
                            const isPdf = /\.pdf$/i.test(url.split('?')[0]);
                            const isWord = /\.(doc|docx)$/i.test(url.split('?')[0]);

                            return (
                              <Grid item xs={12} sm={6} md={4} key={`existing-${idx}`}>
                                <Paper
                                  elevation={3}
                                  sx={{
                                    p: 1,
                                    position: "relative",
                                    borderRadius: 2,
                                    bgcolor: "#e3f2fd",
                                    border: "1px solid #90caf9",
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '70%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                      }}
                                    >
                                      {isImage && <PhotoLibraryIcon sx={{ fontSize: 16, color: "#4caf50" }} />}
                                      {isPdf && <PictureAsPdfIcon sx={{ fontSize: 16, color: "#f44336" }} />}
                                      {isWord && <InsertDriveFileIcon sx={{ fontSize: 16, color: "#2196f3" }} />}
                                      {url.split('/').pop().split('-')[0]} (Old)
                                    </MDTypography>
                                    {(meData?.role_id === 1 || meData?.role_id === 2) && (
                                      <IconButton
                                        size="small"
                                        onClick={() => removeExistingUpload(idx)}
                                        color="error"
                                        sx={{ p: 0.5 }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Box>

                                  {isImage ? (
                                    <Box
                                      sx={{
                                        width: "100%",
                                        height: 150,
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        boxShadow: 1,
                                        border: "1px solid #eee",
                                      }}
                                    >
                                      <img
                                        src={url}
                                        alt="existing"
                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                      />
                                    </Box>
                                  ) : isPdf ? (
                                    <Box
                                      sx={{
                                        width: "100%",
                                        height: 150,
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        boxShadow: 1,
                                        border: "1px solid #eee",
                                      }}
                                    >
                                      <iframe
                                        src={url}
                                        title="existing pdf"
                                        width="100%"
                                        height="100%"
                                        style={{ border: "none" }}
                                      />
                                    </Box>
                                  ) : (
                                    <MDBox display="flex" alignItems="center" justifyContent="center" height={150} bgcolor={isWord ? "#e3f2fd" : "transparent"}>
                                      {isWord ? (
                                        <InsertDriveFileIcon sx={{ fontSize: 60, color: "#2196f3" }} />
                                      ) : (
                                        <VisibilityIcon sx={{ fontSize: 40, color: "#90caf9" }} />
                                      )}
                                    </MDBox>
                                  )}
                                </Paper>
                              </Grid>
                            );
                          })}

                          {/* New Files */}
                          {files.map((file, idx) => {
                            const isImage = file.type.startsWith("image/");
                            const isPdf = file.type === "application/pdf";
                            const isWord = file.type === "application/msword" || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
                            const fileUrl = URL.createObjectURL(file);

                            return (
                              <Grid item xs={12} sm={6} md={4} key={`new-${idx}`}>
                                <Paper
                                  elevation={3}
                                  sx={{
                                    p: 1,
                                    position: "relative",
                                    borderRadius: 2,
                                    bgcolor: "#f8f9fa",
                                    border: "1px solid #ddd",
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                  }}
                                >
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '70%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                      }}
                                    >
                                      {isImage && <PhotoLibraryIcon sx={{ fontSize: 16, color: "#4caf50" }} />}
                                      {isPdf && <PictureAsPdfIcon sx={{ fontSize: 16, color: "#f44336" }} />}
                                      {isWord && <InsertDriveFileIcon sx={{ fontSize: 16, color: "#2196f3" }} />}
                                      {file.name}
                                    </MDTypography>
                                    <IconButton
                                      size="small"
                                      onClick={() => removeFile(idx)}
                                      color="error"
                                      sx={{ p: 0.5 }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>

                                  {isImage ? (
                                    <Box
                                      sx={{
                                        width: "100%",
                                        height: 150,
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        boxShadow: 1,
                                        border: "1px solid #eee",
                                      }}
                                    >
                                      <img
                                        src={fileUrl}
                                        alt={file.name}
                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                      />
                                    </Box>
                                  ) : isPdf ? (
                                    <Box
                                      sx={{
                                        width: "100%",
                                        height: 150,
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        boxShadow: 1,
                                        border: "1px solid #eee",
                                      }}
                                    >
                                      <iframe
                                        src={fileUrl}
                                        title={file.name}
                                        width="100%"
                                        height="100%"
                                        style={{ border: "none" }}
                                      />
                                    </Box>
                                  ) : (
                                    <Box
                                      display="flex"
                                      flexDirection="column"
                                      alignItems="center"
                                      justifyContent="center"
                                      height={150}
                                      bgcolor="#f0f2f5"
                                      borderRadius={1}
                                    >
                                      {isWord ? (
                                        <InsertDriveFileIcon sx={{ fontSize: 60, color: "#2196f3" }} />
                                      ) : (
                                        <MDTypography variant="caption" color="text">
                                          Preview not available
                                        </MDTypography>
                                      )}
                                    </Box>
                                  )}
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </MDBox>
                    )}
                  </Grid>
                </Grid>
              )}

              <MDBox display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <MDButton
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    clearForm();
                    // if (isAdmin) navigate("/events/list");
                  }}
                  sx={{
                    borderRadius: 2,
                    height: "45px",
                    minWidth: 120,
                  }}
                >
                  Cancel
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{ minWidth: { xs: '100%', sm: 160 }, borderRadius: 2 }}
                >
                  {loading ? "Processing..." : isAdmin ? (isEditMode ? "Update Event" : "Create Event") : "Submit Event"}
                </MDButton>
              </MDBox>
            </Stack>
          </MDBox>
        </Card>

        {isAdmin && (
          <MDBox mt={4}>
            <Card>
              <MDBox pt={3} px={3} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h5" fontWeight="bold">
                  Events List
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <MaterialReactTable
                  columns={columns}
                  data={adminData}
                  getRowId={(row) => row.id}
                  rowCount={rowCount}
                  onPaginationChange={setPagination}
                  onSortingChange={setSorting}
                  onColumnFiltersChange={setColumnFilters}
                  state={{
                    pagination,
                    sorting,
                    columnFilters,
                    isLoading: loading,
                  }}
                  enableGlobalFilter={false}
                  enableFullScreenToggle={false}
                  enableDensityToggle={false}
                  enableColumnResizing
                  layoutMode="grid"
                  enableRowNumbers
                  enableStickyHeader
                  manualFiltering
                  manualPagination
                  manualSorting
                  muiTableHeadProps={{
                    sx: {
                      padding: "0px", // won't apply directly to <thead>, but useful for general structure
                      backgroundColor: "#011F3A", // background on <thead> element itself
                    },
                  }}
                  enableColumnActions={false}
                  muiTableHeadCellProps={{
                    sx: {
                      backgroundColor: "#004030",
                      color: "#fff",
                      // fontSize: '16px',
                      fontWeight: "bold",
                      position: "sticky", // Make header sticky
                      top: 0, // Stick it at the top
                      zIndex: 1, // Ensure it stays
                      "& .MuiTableSortLabel-root": {
                        "& .MuiTableSortLabel-icon": {
                          color: "#fff !important",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                        fontSize: "12px",
                        "::placeholder": {
                          color: "#e0e0e0", // Default (blurred) placeholder color
                          opacity: 1,
                        },
                      },
                      "& .MuiInputBase-root.Mui-focused .MuiInputBase-input::placeholder": {
                        color: "#ffffff", // Focused placeholder color
                      },
                      "& .MuiIconButton-root": {
                        color: "#fff",
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "#e0e0e0",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff",
                      },
                      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#fff",
                      },

                      "& .MuiCheckbox-root": {
                        color: "#fff",
                      },
                    },
                  }}
                  muiTableBodyCellProps={{
                    sx: {
                      padding: "8px", // reduce from default 16px
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                  muiTableBodyRowProps={({ row }) => ({
                    sx: {
                      backgroundColor: row.index % 2 === 0 ? "#fafafa" : "#f0f0f0", // soft alternating colors
                    },
                  })}
                  renderTopToolbarCustomActions={({ table }) => (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "1rem",
                        p: "0.5rem",
                        flexWrap: "wrap",
                        marginLeft: "auto",
                      }}
                    >
                      <Tooltip title={"Export data to excel sheet"} arrow>
                        <Button
                          onClick={handleExportData}
                          sx={{
                            background:
                              "linear-gradient(90deg, rgba(2, 183, 255, 0.65) 30%,rgb(110, 199, 235) 100%)",
                            color: "#f4f4f4",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                            transition: "background 0.3s, transform 0.3s",
                            "&:hover": {
                              background:
                                "linear-gradient(90deg, rgba(55, 198, 254, 0.97) 30%,rgb(39, 169, 220) 100%))",
                              transform: "scale(1.05)",
                            },
                            "&:active": {
                              transform: "scale(0.95)",
                            },
                          }}
                          startIcon={<IconTableExport />}
                        >
                          {"Export To Excel"}
                        </Button>
                      </Tooltip>
                    </Box>
                  )}
                  muiTopToolbarProps={{
                    sx: {
                      backgroundColor: "#fff",
                      boxShadow: "none",
                    },
                  }}
                />
              </MDBox>
            </Card>
          </MDBox>
        )}
      </MDBox>

      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: "100%", color: "white !important" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default EventsAdd;
